import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
} from '@vibesites/runtime/react';

// ============================================================================
// Why this fork exists
// ----------------------------------------------------------------------------
// Belmont/rise SpecialOfferBanner functionality on Weidner Classic chrome:
//   1. Tower 12-style primary bar (on-primary copy, secondary Contact Us
//      link), z-index above the header so the opaque header cannot cover it.
//   2. Multi-offer carousel, dismiss X, scroll-collapse into a pill, and
//      clicking the pill re-opens the banner (not a modal).
//
// Palette follows theme tokens: primary band, secondary CTA — same stance
// as Footer / Header.
//
// Maintenance checklist:
//   1. Diff against Belmont SpecialOfferBanner for interaction changes.
//   2. Keep the primary bar, on-primary copy, secondary CTA, and Belmont
//      behaviors (rotation, dismiss, pill → re-open banner, scroll hysteresis).
//   3. Run `pnpm run lint:schema` and `pnpm run typecheck`.
// ============================================================================

const BANNER_BG = 'var(--vibe-color-primary)';
const BANNER_FG = 'var(--vibe-color-text-on-primary)';
const BANNER_CTA = 'var(--vibe-color-secondary)';
const BANNER_DIVIDER_PX = 1;
const SCROLL_HIDE_AT = 200;
const SCROLL_SHOW_AT = 50;

interface SpecialOffer {
  title?: string | null;
  description?: string | null;
  disclaimer?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  enabled?: string | null;
  [key: string]: unknown;
}

const offerSlotDef: SlotGroupDefinition = {
  label: 'Special Offer Content',
  fields: {
    title: {
      label: 'Title',
      type: 'text',
      defaultField: 'special_offers[0].title',
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultField: 'special_offers[0].description',
    },
    disclaimer: {
      label: 'Disclaimer',
      type: 'text',
      defaultField: 'special_offers[0].disclaimer',
    },
  },
  sourceBinding: {
    collection: 'special_offers',
    defaultIndex: 0,
    fieldMappings: {
      title: 'title',
      description: 'description',
      disclaimer: 'disclaimer',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Contact Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function hasValue(v: unknown): v is string {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'null' && s !== 'undefined' && s !== 'false';
}

function isOfferActive(offer: SpecialOffer | null | undefined): boolean {
  if (!offer) return false;

  // NOTE: we intentionally do NOT consult `offer.enabled`. In the data it
  // is a static, always-`false` default, so honoring it would hide every
  // offer. Visibility is driven solely by content + start/end date window.

  if (!hasValue(offer.title) && !hasValue(offer.description)) return false;

  const now = Date.now();

  if (hasValue(offer.start_date)) {
    const start = new Date(offer.start_date!).getTime();
    if (!isNaN(start) && start > now) return false;
  }

  if (hasValue(offer.end_date)) {
    const end = new Date(offer.end_date!).getTime();
    if (!isNaN(end) && end < now) return false;
  }

  return true;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function setBannerHeight(px: number) {
  document.documentElement.style.setProperty('--offer-banner-height', `${px}px`);
}

function offerCopy(offer: SpecialOffer, fallbackDisclaimer?: unknown) {
  const title = hasValue(offer.title) ? offer.title : '';
  const description = hasValue(offer.description) ? stripHtml(offer.description) : '';
  const rawDisclaimer = offer.disclaimer
    ?? (offer as Record<string, unknown>)['disclaimer']
    ?? fallbackDisclaimer
    ?? '';
  const disclaimer = hasValue(rawDisclaimer) ? stripHtml(String(rawDisclaimer)) : '';
  return { title, description, disclaimer };
}

function useOfferRotation(count: number, intervalMs = 6000) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = setInterval(() => setActive((i) => (i + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs, paused]);

  const goTo = useCallback((i: number) => setActive(i), []);
  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);

  return { active, goTo, pause, resume };
}

export function SpecialOfferBanner() {
  const offers = useCollectionBinding<SpecialOffer>('specialofferbanner.items', {
    collection: 'special_offers',
  });
  const offerSlot = useSlotGroup('specialofferbanner.offer', offerSlotDef);
  const cta = useSlotGroup('specialofferbanner.cta', ctaLinkDef);

  const [dismissed, setDismissed] = useState(false);
  const [scrolledAway, setScrolledAway] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const measuredHeight = useRef(0);

  const activeOffers = useMemo(() => {
    const all = offers || [];
    return all.filter(isOfferActive);
  }, [offers]);

  const hasOffers = activeOffers.length > 0;
  const isVisible = hasOffers && animateIn && !dismissed && (!scrolledAway || pinnedOpen);
  const multi = activeOffers.length > 1;
  const showReopenButton = hasOffers && animateIn && !isVisible;

  const { active: activeIdx, goTo, pause, resume } = useOfferRotation(activeOffers.length);

  useEffect(() => {
    if (!hasOffers || !bannerRef.current) {
      setBannerHeight(0);
      return;
    }

    const measure = () => {
      if (!bannerRef.current) return;
      measuredHeight.current = bannerRef.current.scrollHeight;
      if (isVisible) setBannerHeight(measuredHeight.current + BANNER_DIVIDER_PX);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [hasOffers, activeIdx, isVisible]);

  useEffect(() => {
    if (!hasOffers) {
      setBannerHeight(0);
      return;
    }
    const raf = requestAnimationFrame(() => setAnimateIn(true));
    return () => cancelAnimationFrame(raf);
  }, [hasOffers]);

  useEffect(() => {
    setBannerHeight(isVisible ? measuredHeight.current + BANNER_DIVIDER_PX : 0);
  }, [isVisible]);

  useEffect(() => () => setBannerHeight(0), []);

  useEffect(() => {
    if (!hasOffers || dismissed) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < SCROLL_SHOW_AT) {
          setPinnedOpen(false);
          setScrolledAway(false);
        } else if (y > SCROLL_HIDE_AT) {
          setScrolledAway(true);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasOffers, dismissed]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    setPinnedOpen(false);
  }, []);
  const reopenBanner = useCallback(() => {
    setDismissed(false);
    if (typeof window !== 'undefined' && window.scrollY > SCROLL_SHOW_AT) {
      setPinnedOpen(true);
    }
  }, []);

  if (!hasOffers) return null;

  const offer = activeOffers[activeIdx] || activeOffers[0];
  const { title, description, disclaimer } = offerCopy(offer, offerSlot.disclaimer);
  const ctaLabel = cta.label || 'Contact Us';
  const ctaHref = cta.url || '/contact';
  const ctaTarget = cta._target || '_self';

  return (
    <>
      <div
        ref={bannerRef}
        className="sob"
        data-visible={isVisible}
        role="banner"
        aria-label="Special offers"
        onMouseEnter={pause}
        onMouseLeave={resume}
      >
        <div className="sob-inner">
          <div className="sob-content">
            <EditableCollectionBinding bindingPath="specialofferbanner.items" label="Special Offers">
              <EditableSlotGroup groupPath="specialofferbanner.offer" definition={offerSlotDef}>
                <div className="sob-text" key={activeIdx}>
                  {title && (
                    <p className="sob-title font-heading">{title}</p>
                  )}
                  {description && (
                    <p className="sob-desc">{description}</p>
                  )}
                  {disclaimer && (
                    <p className="sob-disclaimer">{disclaimer}</p>
                  )}
                </div>
              </EditableSlotGroup>
            </EditableCollectionBinding>

            <EditableSlotGroup groupPath="specialofferbanner.cta" definition={ctaLinkDef}>
              <a href={ctaHref} target={ctaTarget} className="sob-cta font-body">
                {ctaLabel}
                <span aria-hidden="true"> →</span>
              </a>
            </EditableSlotGroup>

            {multi && (
              <div className="sob-dots" role="tablist" aria-label="Offers">
                {activeOffers.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`sob-dot${i === activeIdx ? ' sob-dot--active' : ''}`}
                    onClick={() => goTo(i)}
                    role="tab"
                    aria-selected={i === activeIdx}
                    aria-label={`Offer ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <button type="button" onClick={handleDismiss} className="sob-close" aria-label="Dismiss">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {multi && isVisible && (
          <div className="sob-progress">
            <div className="sob-progress-bar" key={activeIdx} />
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={reopenBanner}
        className="sob-reopen"
        style={{
          opacity: showReopenButton ? 1 : 0,
          pointerEvents: showReopenButton ? 'auto' : 'none',
          transform: showReopenButton
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(16px)',
        }}
        aria-label="Show special offers"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <span>{activeOffers.length > 1 ? `${activeOffers.length} Special Offers` : 'Special Offer'}</span>
      </button>

      <style>{`
        .sob {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 80;
          background: ${BANNER_BG};
          color: ${BANNER_FG};
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.4s ease;
        }
        .sob[data-visible="true"] {
          max-height: 300px;
          opacity: 1;
          border-bottom: ${BANNER_DIVIDER_PX}px solid ${BANNER_FG};
        }

        .sob-inner {
          position: relative;
          max-width: 80rem;
          margin: 0 auto;
          padding: 0.85rem 4rem 0.85rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .sob-content {
          flex: 1;
          min-width: 0;
          text-align: center;
        }

        .sob-text {
          animation: sob-fade-in 0.4s ease both;
        }
        @keyframes sob-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .sob-title {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 700;
          line-height: 1.35;
          color: ${BANNER_FG};
        }

        .sob-desc {
          margin: 0.2rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.82rem;
          font-weight: 400;
          line-height: 1.4;
          color: ${BANNER_FG};
        }

        .sob-disclaimer {
          margin: 0.2rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          font-weight: 400;
          line-height: 1.4;
          color: ${BANNER_FG};
        }

        .sob-cta {
          display: inline-flex;
          align-items: center;
          margin-top: 0.25rem;
          font-size: 0.92rem;
          font-weight: 600;
          line-height: 1.3;
          color: ${BANNER_CTA};
          text-decoration: none;
        }
        .sob-cta:hover { text-decoration: underline; }

        .sob-dots {
          display: flex;
          justify-content: center;
          gap: 6px;
          margin-top: 0.4rem;
        }
        .sob-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          border: 1.5px solid color-mix(in srgb, ${BANNER_FG} 50%, transparent);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .sob-dot:hover {
          border-color: ${BANNER_FG};
          transform: scale(1.2);
        }
        .sob-dot--active {
          background: ${BANNER_FG};
          border-color: ${BANNER_FG};
        }

        .sob-close {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px; height: 26px;
          border-radius: 50%;
          border: 1.5px solid color-mix(in srgb, ${BANNER_FG} 25%, transparent);
          background: transparent;
          color: color-mix(in srgb, ${BANNER_FG} 70%, transparent);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.2s;
        }
        @media (min-width: 640px) { .sob-close { right: 1.5rem; } }
        @media (min-width: 1024px) { .sob-close { right: 2rem; } }
        .sob-close:hover {
          background: color-mix(in srgb, ${BANNER_FG} 15%, transparent);
          color: ${BANNER_FG};
          border-color: color-mix(in srgb, ${BANNER_FG} 50%, transparent);
          transform: translateY(-50%) scale(1.1);
        }

        .sob-progress {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 2px;
          background: color-mix(in srgb, ${BANNER_FG} 10%, transparent);
        }
        .sob-progress-bar {
          height: 100%;
          background: ${BANNER_CTA};
          animation: sob-progress-fill 6s linear forwards;
        }
        @keyframes sob-progress-fill {
          from { width: 0; }
          to   { width: 100%; }
        }

        .sob-reopen {
          position: fixed;
          bottom: 1.25rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 90;
          display: flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.6rem 1.25rem;
          border-radius: 9999px;
          border: none;
          background: ${BANNER_BG};
          color: ${BANNER_FG};
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.025em;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25);
          transition: opacity 0.35s ease, transform 0.35s ease, box-shadow 0.2s;
        }
        .sob-reopen svg { color: ${BANNER_CTA}; }
        .sob-reopen:hover {
          box-shadow: 0 6px 24px rgba(0,0,0,0.35);
          transform: translateX(-50%) translateY(-3px) !important;
        }

        @media (max-width: 640px) {
          .sob-inner { padding: 0.7rem 3rem 0.7rem 0.75rem; gap: 0.5rem; }
          .sob-title { font-size: 0.95rem; }
          .sob-desc { font-size: 0.75rem; }
          .sob-disclaimer { font-size: 0.72rem; }
          .sob-cta { font-size: 0.85rem; }
          .sob-dots { gap: 5px; }
          .sob-dot { width: 6px; height: 6px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .sob, .sob-reopen, .sob-text { transition: none; animation: none; }
          .sob-progress-bar { animation: none; width: 100%; }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOfferBanner',
  displayName: 'Special Offer Banner',
  tokens: {},
  slotGroups: {
    'specialofferbanner.offer': offerSlotDef,
    'specialofferbanner.cta': ctaLinkDef,
  },
  collectionBindings: {
    'specialofferbanner.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(SpecialOfferBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SpecialOfferBanner;
